import { useCallback, useEffect, useMemo, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Autocomplete, Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useUnit } from 'effector-react';
import { Controller, useForm } from 'react-hook-form';

import { useStrategyInfo, useUpdateStrategy } from './queries';
import { createValidationSchema } from './schema';
import { normalizeEditValues, toLocalInputDate } from './submitHelpers';
import { getClientInfo } from '../../../../../../api';
import { ControlledAgreementListField } from '../../../../../../components/FormControlsV2/ControlledAgreementListField';
import { ControlledCheckboxField } from '../../../../../../components/FormControlsV2/ControlledCheckboxField';
import { ControlledCodeWeightsField } from '../../../../../../components/FormControlsV2/ControlledCodeWeightsField';
import { ControlledFileField } from '../../../../../../components/FormControlsV2/ControlledFileField';
import { ControlledInfoPartsField } from '../../../../../../components/FormControlsV2/ControlledInfoPartsField';
import { ControlledTextEditorField } from '../../../../../../components/FormControlsV2/ControlledTextEditorField';
import { ControlledTextField } from '../../../../../../components/FormControlsV2/ControlledTextField';
import { FormProvider } from '../../../../../../components/FormControlsV2/FormProvider';
import { ImagePreview } from '../../../../../../components/UIKit/ImagePreview';
import { VideoPreview } from '../../../../../../components/UIKit/VideoPreview';
import { categories, recalcModes } from '../../../../../../const/strategyRefs';
import { ERRORS_TEXTS } from '../../../../../../const/validation';
import { strategyInfoRef$ } from '../../../../../../effector/aggregateStrategyInfoRefs';
import { codes$ } from '../../../../../../effector/classCodes';
import { Config$ } from '../../../../../../effector/config';
import { currencyInfo$ } from '../../../../../../effector/currencies';
import { useAllUsers } from '../../../../../../hooks/useUsers';
import { StrategyStatus } from '../../../../../../types/strategy';
import { isErrorResponse, parseApiError } from '../../../../../../utils/apiErrors';
import {
  autoconsaltKeyPrefix,
  autofollowKeyPrefix,
  getDefaultEditStrategyFormValues,
  initializeServicePackageTariffFields,
  mapFormValuesToUpdateRequest,
  mapStrategyToFormValues,
} from '../EditShared/mappers';
import { useServicePackages, useTariffs } from '../EditShared/queries';
import { EditStrategyFormFields } from '../EditShared/types';

type Props = {
  strategyId: string;
  onClose: () => void;
  onUpdate: () => void;
  notify: (msg: string) => void;
};

const getSecurityInfo = async (securities: string[]) => ({
  success: true,
  result: securities.map((item) => ({ id: item, name: item, value: item })),
});

const getErrorMessage = (error: unknown): string => {
  if (!error) {
    return '';
  }

  const asRecord = error as { message?: string };
  return asRecord.message || '';
};

const renderAutocompleteInput = (params: object, label: string, fieldError?: unknown) => (
  <TextField
    {...(params as Record<string, unknown>)}
    label={label}
    margin="normal"
    error={Boolean(fieldError)}
    helperText={getErrorMessage(fieldError)}
  />
);

const twoColumnFieldsGridSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
  gap: 2,
  '& .MuiFormControl-root': {
    margin: 0,
  },
  '& .MuiTextField-root': {
    margin: 0,
  },
};

export function EditStrategyDialogForm({ strategyId, onClose, onUpdate, notify }: Props) {
  const [error, setError] = useState('');
  const [isVideoChanged, setIsVideoChanged] = useState(false);

  const infoStore = useUnit(strategyInfoRef$);
  const currencyStore = useUnit(currencyInfo$);
  const classCodesStore = useUnit(codes$);
  const configStore = useUnit(Config$);
  const { data: packages } = useServicePackages();
  const { data: tariffsResult } = useTariffs();
  const { data: strategy, isLoading: isLoadingStrategy, error: strategyError } = useStrategyInfo(strategyId);
  const { mutateAsync: updateStrategyMutation } = useUpdateStrategy();
  const queryClient = useQueryClient();
  const { authors } = useAllUsers();

  const defaultValues = useMemo(() => getDefaultEditStrategyFormValues(), []);
  const validationSchema = useMemo(
    () => createValidationSchema(strategy?.status === StrategyStatus.Standard),
    [strategy?.status],
  );

  const users = useMemo(() => authors.map((u) => ({ id: String(u.id), name: u.name })), [authors]);

  const {
    control,
    reset,
    setValue,
    setError: setFieldError,
    clearErrors,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<EditStrategyFormFields>({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(validationSchema) as never,
  });

  useEffect(() => {
    if (!strategy || infoStore.size === 0 || !tariffsResult || tariffsResult.length === 0) {
      return;
    }

    const mapped = mapStrategyToFormValues({
      strategy,
      infoStore: infoStore.data,
      currencyStore,
      users: users,
      tariffs: tariffsResult || [],
      recalcModes,
      categories,
    });

    reset({
      ...defaultValues,
      ...mapped,
    });
    setIsVideoChanged(false);
  }, [defaultValues, infoStore, currencyStore, reset, strategy, tariffsResult, users]);

  useEffect(() => {
    if (!strategy || !tariffsResult || tariffsResult.length === 0) {
      return;
    }

    const tariffs = tariffsResult.map(({ id, description }) => ({ id, name: description }));
    initializeServicePackageTariffFields(strategy.servicePackageTariffs || [], tariffs, (field, value) => {
      setValue(field as never, value as never);
    });
  }, [setValue, strategy, tariffsResult]);

  const classCodeOptions = useMemo(
    () => classCodesStore.map((code) => ({ value: code, label: code })),
    [classCodesStore],
  );

  const afTariffs = useMemo(
    () =>
      tariffsResult
        ?.filter((tariff) => tariff.kind === 1)
        .map((tariff) => ({ id: tariff.id, name: tariff.description, guid: tariff.guid, category: tariff.category })) ||
      [],
    [tariffsResult],
  );

  const acTariffs = useMemo(
    () =>
      tariffsResult
        ?.filter((tariff) => tariff.kind === 2)
        .map((tariff) => ({ id: tariff.id, name: tariff.description, guid: tariff.guid })) || [],
    [tariffsResult],
  );

  const afTariffsPs = useMemo(
    () =>
      tariffsResult
        ?.filter((tariff) => tariff.kind === 1)
        .map((tariff) => ({ id: tariff.id, name: tariff.description })) || [],
    [tariffsResult],
  );

  const acTariffsPs = useMemo(
    () =>
      tariffsResult
        ?.filter((tariff) => tariff.kind === 2)
        .map((tariff) => ({ id: tariff.id, name: tariff.description })) || [],
    [tariffsResult],
  );

  const tradeTypes = useMemo(
    () =>
      infoStore.data.tradeTypes.filter((type) => type.enabled).map((type) => ({ id: type.id, name: type.description })),
    [infoStore.data.tradeTypes],
  );

  const isRestricted = watch('isRestricted');
  const isAutofollowPs = watch('isAutofollowPS');
  const isAutoconsultPs = watch('isAutoconsultPS');
  const imageUrl = watch('imageUrl');
  const videoUrl = watch('videoUrl');
  const matchmakerVideoUrl = watch('matchmakerVideoUrl');
  const friendlyUrl = configStore?.friendlyUrl;

  const verifyClientInfo = useCallback(
    (agreementNumbers: string[]) =>
      queryClient.fetchQuery({
        queryKey: ['getClientInfo', agreementNumbers],
        queryFn: () => getClientInfo(agreementNumbers),
        staleTime: 5 * 60 * 1000,
      }),
    [queryClient],
  );

  const clearPackageTariffErrors = () => {
    (packages || []).forEach((pkg) => {
      clearErrors(`${autofollowKeyPrefix}${pkg.id}` as never);
      clearErrors(`${autoconsaltKeyPrefix}${pkg.id}` as never);
    });
  };

  const validatePackageTariffs = (values: EditStrategyFormFields): boolean => {
    let isValid = true;
    const valuesRecord = values as Record<string, unknown>;

    (packages || []).forEach((pkg) => {
      const autofollowField = `${autofollowKeyPrefix}${pkg.id}`;
      const autoconsultField = `${autoconsaltKeyPrefix}${pkg.id}`;

      if (values.isAutofollowPS && !valuesRecord[autofollowField]) {
        setFieldError(autofollowField as never, { type: 'manual', message: ERRORS_TEXTS.required });
        isValid = false;
      }

      if (values.isAutoconsultPS && !valuesRecord[autoconsultField]) {
        setFieldError(autoconsultField as never, { type: 'manual', message: ERRORS_TEXTS.required });
        isValid = false;
      }
    });

    return isValid;
  };

  const onSubmit = async (values: EditStrategyFormFields) => {
    setError('');
    clearPackageTariffErrors();

    if (!validatePackageTariffs(values)) {
      return;
    }

    const normalizedValues = normalizeEditValues(values);

    try {
      const data = mapFormValuesToUpdateRequest(normalizedValues, { packages: packages || [] });
      const result = await updateStrategyMutation(data);

      if (isErrorResponse(result)) {
        setError(parseApiError(result));
        return;
      }

      notify('Изменения сохранены');
      onUpdate();
    } catch (err) {
      setError(parseApiError(err));
    }
  };

  if (strategyError) {
    return (
      <Alert severity="error">
        Стратегия с идентификатором {strategyId} не существует: {parseApiError(strategyError)}
      </Alert>
    );
  }

  if (isLoadingStrategy || !strategy || infoStore.size === 0 || !tariffsResult || tariffsResult.length === 0) {
    return (
      <Box sx={{ py: 2, textAlign: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} autoComplete="off" noValidate>
      <FormProvider control={control} errors={errors}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Описание
        </Typography>

        <ControlledTextField name="title" label="Название" />
        <ControlledTextField name="tagline" label="Слоган" />

        <Controller
          name="author"
          control={control}
          render={({ field }) => (
            <Autocomplete
              options={users}
              value={field.value}
              onChange={(_, value) => field.onChange(value)}
              getOptionLabel={(option) => option?.name || ''}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              renderInput={(params) => renderAutocompleteInput(params, 'Автор', errors.author)}
            />
          )}
        />

        <Controller
          name="managerIds"
          control={control}
          render={({ field }) => (
            <Autocomplete
              multiple
              options={users}
              value={field.value || []}
              onChange={(_, value) => field.onChange(value)}
              getOptionLabel={(option) => option?.name || ''}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              renderInput={(params) => renderAutocompleteInput(params, 'Управляющие', errors.managerIds)}
            />
          )}
        />

        <ControlledCheckboxField name="isBlogger" label="Стратегия блогера" />

        {friendlyUrl && imageUrl && (
          <ImagePreview image={`${friendlyUrl}${imageUrl}`} onDelete={() => setValue('imageUrl', '')} />
        )}

        <ControlledFileField
          name="imageUrl"
          placeholder="Большое изображение"
          url="api/admin/files/upload_strategy_picture"
          fileTypes={['.png', '.jpg', '.jpeg', '.gif']}
          sizeView="mb"
          uploadedFileName={imageUrl ?? undefined}
          onUpload={(_, fileName) => setValue('imageUrl', fileName as never, { shouldDirty: true })}
          onRemove={() => setValue('imageUrl', '', { shouldDirty: true })}
        />

        {videoUrl && (
          <VideoPreview
            video={videoUrl}
            baseUrl={friendlyUrl}
            onDelete={() => {
              setIsVideoChanged(true);
              setValue('videoUrl', '', { shouldDirty: true });
            }}
          />
        )}

        <ControlledFileField
          name="videoUrl"
          placeholder="Видео стратегии"
          url={`api/admin/files/upload_strategy_video/${strategyId}`}
          fileTypes={['.mp4', '.webm', '.avi', '.mov', '.mkv']}
          sizeView="mb"
          maxFileSizeMb={100}
          uploadedFileName={videoUrl ?? undefined}
          onUpload={(_, fileName) => {
            setIsVideoChanged(true);
            setValue('videoUrl', fileName as never, { shouldDirty: true });
          }}
          onRemove={() => {
            setIsVideoChanged(true);
            setValue('videoUrl', '', { shouldDirty: true });
          }}
        />

        {matchmakerVideoUrl && (
          <VideoPreview
            video={matchmakerVideoUrl}
            baseUrl={friendlyUrl}
            title="Загруженное вертикальное видео для подборщика"
            aspectRatio="9 / 16"
            maxWidth={360}
            onDelete={() => {
              setIsVideoChanged(true);
              setValue('matchmakerVideoUrl', '', { shouldDirty: true });
            }}
          />
        )}

        <ControlledFileField
          name="matchmakerVideoUrl"
          placeholder="Вертикальное видео для финального экрана подборщика"
          url={`api/admin/files/upload_strategy_matchmaker_video/${strategyId}`}
          fileTypes={['.mp4', '.webm', '.avi', '.mov', '.mkv']}
          sizeView="mb"
          maxFileSizeMb={100}
          uploadedFileName={matchmakerVideoUrl ?? undefined}
          onUpload={(_, fileName) => {
            setIsVideoChanged(true);
            setValue('matchmakerVideoUrl', fileName as never, { shouldDirty: true });
          }}
          onRemove={() => {
            setIsVideoChanged(true);
            setValue('matchmakerVideoUrl', '', { shouldDirty: true });
          }}
        />

        <ControlledTextEditorField name="description" label="Описание" />
        <ControlledInfoPartsField name="infoParts" label="Преимущества" />
        <ControlledInfoPartsField name="riskReasons" label="Риски" />
        <ControlledTextEditorField name="disclaimer" label="Дисклеймер" />
        <ControlledTextEditorField name="welcomeLetter" label="Текст для Welcome letter" />
        <ControlledTextField name="subscription" label="Подпись под графиком" />

        <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
          Характеристики
        </Typography>

        <Box sx={{ ...twoColumnFieldsGridSx, mt: 2 }}>
          <Controller
            name="profile"
            control={control}
            render={({ field }) => (
              <Autocomplete
                options={infoStore.data.profiles}
                value={field.value || null}
                onChange={(_, value) => field.onChange(value)}
                getOptionLabel={(option) => option?.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                renderInput={(params) => renderAutocompleteInput(params, 'Инвестпрофиль', errors.profile)}
              />
            )}
          />

          <Controller
            name="currency"
            control={control}
            render={({ field }) => (
              <Autocomplete
                options={currencyStore}
                value={field.value || null}
                onChange={(_, value) => field.onChange(value)}
                getOptionLabel={(option) => option?.name || ''}
                isOptionEqualToValue={(option, value) => option.code === value?.code}
                renderInput={(params) => renderAutocompleteInput(params, 'Валюта', errors.currency)}
              />
            )}
          />
        </Box>

        <Box sx={{ ...twoColumnFieldsGridSx, mt: 2 }}>
          <ControlledTextField type="number" name="minSumma" label="Минимальная сумма" />
          <ControlledTextField type="number" name="recommendedAmount" label="Рекомендуемая сумма" />
          <ControlledTextField type="number" name="capacity" label="Ёмкость" />
          <ControlledTextField type="number" name="estimatedProfit" label="Прогнозная доходность" />
        </Box>

        <Box sx={{ ...twoColumnFieldsGridSx, mt: 2 }}>
          <ControlledTextField type="number" name="estimatedTradesPerWeek" label="Прогнозная частота сделок" />

          <Controller
            name="period"
            control={control}
            render={({ field }) => (
              <Autocomplete
                options={infoStore.data.durations}
                value={field.value || null}
                onChange={(_, value) => field.onChange(value)}
                getOptionLabel={(option) => option?.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                renderInput={(params) => renderAutocompleteInput(params, 'Срок', errors.period)}
              />
            )}
          />

          <Controller
            name="category"
            control={control}
            render={({ field }) => (
              <Autocomplete
                options={categories}
                value={field.value || null}
                onChange={(_, value) => field.onChange(value)}
                getOptionLabel={(option) => option?.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                renderInput={(params) => renderAutocompleteInput(params, 'Категория', errors.category)}
              />
            )}
          />

          <Controller
            name="index"
            control={control}
            render={({ field }) => (
              <Autocomplete
                options={infoStore.data.indexes}
                value={field.value || null}
                onChange={(_, value) => field.onChange(value)}
                getOptionLabel={(option) => option?.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                renderInput={(params) => renderAutocompleteInput(params, 'Бенчмарк', errors.index)}
              />
            )}
          />
        </Box>

        <ControlledCheckboxField name="hideIndexOnChart" label="Скрывать индекс на графике" />
        <ControlledCheckboxField name="hideIndexInWL" label="Скрывать индекс в Welcome letter" />

        <Box sx={twoColumnFieldsGridSx}>
          <Controller
            name="markets"
            control={control}
            render={({ field }) => (
              <Autocomplete
                multiple
                options={infoStore.data.markets}
                value={field.value || []}
                onChange={(_, value) => field.onChange(value)}
                getOptionLabel={(option) => option?.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                renderInput={(params) => renderAutocompleteInput(params, 'Рынки', errors.markets)}
              />
            )}
          />

          <Controller
            name="tools"
            control={control}
            render={({ field }) => (
              <Autocomplete
                multiple
                options={infoStore.data.marketTools}
                value={field.value || []}
                onChange={(_, value) => field.onChange(value)}
                getOptionLabel={(option) => option?.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                renderInput={(params) => renderAutocompleteInput(params, 'Площадки', errors.tools)}
              />
            )}
          />

          <Controller
            name="boards"
            control={control}
            render={({ field }) => (
              <Autocomplete
                multiple
                options={infoStore.data.boards}
                value={field.value || []}
                onChange={(_, value) => field.onChange(value)}
                getOptionLabel={(option) => option?.code || ''}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                renderInput={(params) => renderAutocompleteInput(params, 'Торговые системы', errors.boards)}
              />
            )}
          />

          <Controller
            name="tests"
            control={control}
            render={({ field }) => (
              <Autocomplete
                multiple
                options={infoStore.data.tests}
                value={field.value || []}
                onChange={(_, value) => field.onChange(value)}
                getOptionLabel={(option) => option?.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                renderInput={(params) => renderAutocompleteInput(params, 'Тесты стратегии', errors.tests)}
              />
            )}
          />
        </Box>

        <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
          Инструменты и услуги
        </Typography>

        <ControlledAgreementListField
          name="securities"
          label="Инструменты"
          inputLabel="Маска инструмента"
          verify={getSecurityInfo}
          verifyError="Не удается определить код инструмента"
          useKeyValue={false}
        />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 2,
          }}
        >
          <Box>
            <ControlledCheckboxField name="isAutofollow" label="Автоследование" />
            <Controller
              name="tariffAf"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  options={afTariffs}
                  value={field.value || null}
                  onChange={(_, value) => field.onChange(value)}
                  getOptionLabel={(option) => option?.name || ''}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  renderInput={(params) => renderAutocompleteInput(params, 'Тариф АФ', errors.tariffAf)}
                />
              )}
            />
          </Box>

          <Box>
            <ControlledCheckboxField name="isAutoconsult" label="Автоконсультирование" />
            <Controller
              name="tariffAc"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  options={acTariffs}
                  value={field.value || null}
                  onChange={(_, value) => field.onChange(value)}
                  getOptionLabel={(option) => option?.name || ''}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  renderInput={(params) => renderAutocompleteInput(params, 'Тариф АК', errors.tariffAc)}
                />
              )}
            />
          </Box>
        </Box>

        <ControlledCheckboxField name="isAutofollowPS" label="Автоследование по пакету услуг" />
        {isAutofollowPs &&
          (packages || []).map((pkg) => {
            const fieldName = `${autofollowKeyPrefix}${pkg.id}`;
            return (
              <Controller
                key={fieldName}
                name={fieldName as never}
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    options={afTariffsPs}
                    value={(field.value as { id: number; name: string } | null) || null}
                    onChange={(_, value) => {
                      clearErrors(fieldName as never);
                      field.onChange(value);
                    }}
                    getOptionLabel={(option) => option?.name || ''}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    renderInput={(params) =>
                      renderAutocompleteInput(
                        params,
                        `Пакет ${pkg.name} (АФ)`,
                        (errors as Record<string, unknown>)[fieldName],
                      )
                    }
                  />
                )}
              />
            );
          })}

        <ControlledCheckboxField name="isAutoconsultPS" label="Автоконсультирование по пакету услуг" />
        {isAutoconsultPs &&
          (packages || []).map((pkg) => {
            const fieldName = `${autoconsaltKeyPrefix}${pkg.id}`;
            return (
              <Controller
                key={fieldName}
                name={fieldName as never}
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    options={acTariffsPs}
                    value={(field.value as { id: number; name: string } | null) || null}
                    onChange={(_, value) => {
                      clearErrors(fieldName as never);
                      field.onChange(value);
                    }}
                    getOptionLabel={(option) => option?.name || ''}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    renderInput={(params) =>
                      renderAutocompleteInput(
                        params,
                        `Пакет ${pkg.name} (АК)`,
                        (errors as Record<string, unknown>)[fieldName],
                      )
                    }
                  />
                )}
              />
            );
          })}

        <Box sx={twoColumnFieldsGridSx}>
          <ControlledTextField type="number" name="rateShort" label="Ставка для короткой позиции" />
          <ControlledTextField type="number" name="rateLong" label="Ставка для длинной позиции" />
        </Box>

        <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
          Площадки EVA
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' },
            gap: 1.5,
          }}
        >
          {[
            { name: 'stockMarketMMVB', label: 'StockMarketMMVB' },
            { name: 'fortsRTS', label: 'FortsRTS' },
            { name: 'creditGO', label: 'CreditGO' },
            { name: 'currencyMarket', label: 'CurrencyMarket' },
            { name: 'spbRTS', label: 'SpbRTS' },
            { name: 'currencyMarketEBS', label: 'CurrencyMarketEBS' },
            { name: 'stockMarketOffexchange', label: 'StockMarketOffexchange' },
            { name: 'mmvbUSD', label: 'MmvbUSD' },
            { name: 'fortsRTSEBS', label: 'FortsRTSEBS' },
            { name: 'lse', label: 'Lse' },
            { name: 'usMarkets', label: 'UsMarkets' },
          ].map((item) => (
            <Box key={item.name}>
              <ControlledCheckboxField name={item.name} label={item.label} fullWidth />
            </Box>
          ))}
        </Box>

        <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
          Параметры
        </Typography>

        <Box sx={twoColumnFieldsGridSx}>
          <ControlledTextField type="number" name="maxInstrumentWeight" label="Максимальная доля инструмента, %" />
          <ControlledTextField
            type="number"
            name="maxSignalWeight"
            label="Максимальная доля инструмента в сигнале, %"
          />
        </Box>
        <ControlledCodeWeightsField
          name="maxSignalWeights"
          label="Максимальные доли типов в сигнале, %"
          codeOptions={classCodeOptions}
        />
        <ControlledTextField type="number" name="leverage" label="Используемое плечо" />
        <ControlledTextField type="number" name="shortLeverage" label="Плечо для шорта" />
        <ControlledCodeWeightsField
          name="maxInstrumentWeights"
          label="Максимальная доля по ClassCode, %"
          codeOptions={classCodeOptions}
        />

        <ControlledCheckboxField name="isAlgo" label="Без пересчета весов" />
        <ControlledCheckboxField name="testMode" label="Тестовая" />

        <Box sx={twoColumnFieldsGridSx}>
          <Controller
            name="plStartDate"
            control={control}
            render={({ field }) => (
              <TextField
                label="Начальная дата расчета доходности"
                type="date"
                margin="normal"
                fullWidth
                value={toLocalInputDate(field.value)}
                onChange={(event) => {
                  const value = event.target.value;
                  field.onChange(value ? new Date(`${value}T00:00:00`) : undefined);
                }}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />

          <Controller
            name="publishingDate"
            control={control}
            render={({ field }) => (
              <TextField
                label="Дата начала действия продукта"
                type="date"
                margin="normal"
                fullWidth
                value={toLocalInputDate(field.value)}
                onChange={(event) => {
                  const value = event.target.value;
                  field.onChange(value ? new Date(`${value}T00:00:00`) : undefined);
                }}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />

          <Controller
            name="recalcMode"
            control={control}
            render={({ field }) => (
              <Autocomplete
                options={recalcModes}
                value={field.value || null}
                onChange={(_, value) => field.onChange(value)}
                getOptionLabel={(option) => option?.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                renderInput={(params) => renderAutocompleteInput(params, 'Пересчет весов', errors.recalcMode)}
              />
            )}
          />

          <Controller
            name="tradeType"
            control={control}
            render={({ field }) => (
              <Autocomplete
                options={tradeTypes}
                value={field.value || null}
                onChange={(_, value) => field.onChange(value)}
                getOptionLabel={(option) => option?.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                renderInput={(params) => renderAutocompleteInput(params, 'Тип проторговки', errors.tradeType)}
              />
            )}
          />

          <ControlledTextField type="number" name="codeDWH" label="ID КХД" />
          <ControlledTextField type="number" name="productPlanKO" label="KO продукта" />
        </Box>

        <ControlledCheckboxField name="isRestricted" label="Закрытая" />
        <ControlledAgreementListField
          name="restrictedClients"
          label="Ограниченные клиенты"
          inputLabel="Номер соглашения"
          verify={verifyClientInfo}
          verifyError="Не удается определить клиента по номеру соглашения"
          disabled={!isRestricted}
        />

        <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
          Отображение
        </Typography>

        <ControlledCheckboxField name="isIis" label="Доступно для ИИС" />
        <ControlledCheckboxField name="isRecommended" label="Рекомендуем" />
        <ControlledCheckboxField name="isHidden" label="Скрывать состав" />
        <ControlledCheckboxField name="repoWithCcp" label="РЕПО с ЦК" />
      </FormProvider>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <Stack spacing={1.5} sx={{ mt: 3 }}>
        <Button type="button" variant="outlined" onClick={onClose} disabled={isSubmitting} fullWidth>
          Отмена
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={isSubmitting || (!isDirty && !isVideoChanged)}
          fullWidth
          startIcon={isSubmitting && <CircularProgress size={18} />}
        >
          Сохранить
        </Button>
      </Stack>
    </form>
  );
}
